﻿/**
 * Component: Product Form
 * Description: Shared PDP interactions for variants, galleries, and AJAX cart.
 */

(function () {
    'use strict';

    const PRODUCT_SECTION_SELECTOR = '[data-product-section]';

    window.productStrings = window.productStrings || {
        addToCart: 'ADD TO CART',
        soldOut: 'SOLD OUT'
    };

    const getVariantData = (section) => {
        const sectionId = section.dataset.productSection || section.dataset.sectionId;
        const variantDataEl = document.getElementById(`ProductVariantData-${sectionId}`);
        if (variantDataEl) {
            try {
                return JSON.parse(variantDataEl.textContent);
            } catch (error) {
                console.warn('ProductVariantData parse failed.', error);
            }
        }

        const legacyDataEl = document.getElementById(`ProductJson-${sectionId}`) || document.querySelector('[id^="ProductJson-"]');
        if (!legacyDataEl) return { sectionId, variants: [] };

        try {
            const productJson = JSON.parse(legacyDataEl.textContent);
            return {
                sectionId,
                variants: (productJson.variants || []).map((variant) => ({
                    id: variant.id,
                    title: variant.title,
                    available: variant.available,
                    options: variant.options || [],
                    price: variant.price,
                    price_formatted: null,
                    compare_at_price: variant.compare_at_price || 0,
                    compare_at_price_formatted: null,
                    sku: variant.sku || '',
                    featured_media_id: variant.featured_media ? variant.featured_media.id : null,
                    featured_image_src: variant.featured_image ? variant.featured_image.src : null,
                    featured_image_ratio: variant.featured_image ? variant.featured_image.aspect_ratio : null
                }))
            };
        } catch (error) {
            console.warn('ProductJson parse failed.', error);
            return { sectionId, variants: [] };
        }
    };

    const formatMoneyFallback = (value, referenceNode) => {
        const numeric = Number(value || 0) / 100;
        const referenceText = referenceNode ? referenceNode.textContent.trim() : '';
        const symbolMatch = referenceText.match(/[^\d\s.,]+/);
        const symbol = symbolMatch ? symbolMatch[0] : '$';
        return `${symbol}${numeric.toFixed(2)}`;
    };

    const formatMoney = (value, referenceNode, preformatted) => {
        if (preformatted) return preformatted;

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            try {
                return window.Shopify.formatMoney(value);
            } catch (error) {
                return formatMoneyFallback(value, referenceNode);
            }
        }

        return formatMoneyFallback(value, referenceNode);
    };

    const updateGalleryCounter = (track, counter) => {
        if (!track || !counter || !track.children.length) return;
        const step = track.offsetWidth || 1;
        const nextIndex = Math.max(1, Math.min(track.children.length, Math.round(track.scrollLeft / step) + 1));
        counter.textContent = String(nextIndex);
    };

    const syncGalleryCounter = (section) => {
        const track = section.querySelector('[data-product-gallery-track]');
        const counter = section.querySelector('[data-product-gallery-counter]');

        if (!track || !counter || track.dataset.galleryCounterBound === 'true') return;

        let debounceId;
        const onScroll = () => {
            clearTimeout(debounceId);
            debounceId = window.setTimeout(() => updateGalleryCounter(track, counter), 60);
        };

        track.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', () => updateGalleryCounter(track, counter), { passive: true });
        updateGalleryCounter(track, counter);
        track.dataset.galleryCounterBound = 'true';
    };

    const getSelectedVariant = (section) => {
        const select = section.querySelector('[name="id"]');
        if (!select) return null;

        const variantData = section._productVariantData || { variants: [] };
        const selectedId = Number(select.value);
        return variantData.variants.find((variant) => Number(variant.id) === selectedId) || null;
    };

    const dispatchVariantChanged = (variant) => {
        if (!variant) return;

        document.dispatchEvent(new CustomEvent('variant:changed', {
            detail: { variant },
            bubbles: true
        }));
    };

    const updateTemplate2MainImage = (section, variant) => {
        const sectionId = section.dataset.productSection || section.dataset.sectionId;
        const mainWrap = document.getElementById(`PT2MainImage-${sectionId}`);
        const mainImg = document.getElementById(`PT2MainImg-${sectionId}`);

        if (!mainImg || !variant || !variant.featured_image_src) return;

        const baseSrc = variant.featured_image_src.replace(/\?.*/, '');
        mainImg.style.opacity = '0';

        window.setTimeout(() => {
            if (mainWrap && variant.featured_image_ratio) {
                mainWrap.style.setProperty('--media-ratio', variant.featured_image_ratio);
            }
            mainImg.src = `${baseSrc}?width=1100`;
            mainImg.srcset = `${baseSrc}?width=375 375w, ${baseSrc}?width=550 550w, ${baseSrc}?width=750 750w, ${baseSrc}?width=1100 1100w`;
            mainImg.style.opacity = '1';
        }, 160);
    };

    const updateOptionButtons = (section, variant) => {
        if (!variant) return;

        const groups = section.querySelectorAll('.variant-options');
        groups.forEach((group, index) => {
            const buttons = group.querySelectorAll('.variant-option-btn');
            buttons.forEach((button) => {
                const isSelected = (variant.options[index] || '') === (button.dataset.value || '').trim();
                button.classList.toggle('selected', isSelected);
                button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });
        });
    };

    const updateVariantCards = (section, variant) => {
        if (!variant) return;

        const cards = section.querySelectorAll('.pt2-vcard');
        cards.forEach((card) => {
            const isSelected = Number(card.dataset.variantId) === Number(variant.id);
            card.classList.toggle('selected', isSelected);
            card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
    };

    const updatePriceUi = (section, variant) => {
        const priceEl = section.querySelector('[data-product-price]');
        const compareEl = section.querySelector('[data-product-compare-price]');
        const savingsEl = section.querySelector('[id^="PT2PriceSavings-"]');

        if (priceEl) {
            priceEl.textContent = formatMoney(variant.price, priceEl, variant.price_formatted);
        }

        if (compareEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                compareEl.style.display = '';
                compareEl.textContent = formatMoney(
                    variant.compare_at_price,
                    compareEl,
                    variant.compare_at_price_formatted
                );
            } else {
                compareEl.style.display = 'none';
            }
        }

        if (savingsEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                const savingsAmount = variant.compare_at_price - variant.price;
                const savingsPct = Math.round((savingsAmount * 100) / variant.compare_at_price);
                savingsEl.style.display = '';
                savingsEl.textContent = `Save ${formatMoney(savingsAmount, savingsEl)} (${savingsPct}%)`;
            } else {
                savingsEl.style.display = 'none';
            }
        }
    };

    const updateAddButtonUi = (section, variant) => {
        const addButton = section.querySelector('[data-product-add-button]');
        const addText = section.querySelector('[data-product-add-text]');

        if (!addButton || !variant) return;

        const addLabel = addButton.dataset.addLabel || window.productStrings.addToCart;
        const soldOutLabel = addButton.dataset.soldOutLabel || window.productStrings.soldOut;

        addButton.dataset.variantId = variant.id;
        addButton.dataset.variantAvailable = variant.available ? 'true' : 'false';
        addButton.disabled = !variant.available;
        addButton.classList.toggle('disabled', !variant.available);

        if (addText) {
            addText.textContent = variant.available
                ? `${addLabel} — ${formatMoney(variant.price, addText, variant.price_formatted)}`
                : soldOutLabel;
        }
    };

    const updateHiddenSelect = (section, variant) => {
        const select = section.querySelector('[name="id"]');
        if (select && variant) {
            select.value = variant.id;
        }
    };

    const updateUrl = (variant) => {
        if (!variant || !variant.id) return;

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', nextUrl);
    };

    const applyVariant = (section, variant, options) => {
        if (!section || !variant) return;

        updateHiddenSelect(section, variant);
        updatePriceUi(section, variant);
        updateAddButtonUi(section, variant);

        if (section.dataset.variantUi === 'options') {
            updateOptionButtons(section, variant);
        }

        if (section.dataset.variantUi === 'cards') {
            updateVariantCards(section, variant);
            updateTemplate2MainImage(section, variant);
        }

        if (!options || options.updateUrl !== false) {
            updateUrl(variant);
        }

        if (!options || options.dispatchEvent !== false) {
            dispatchVariantChanged(variant);
        }
    };

    const findVariantByOptions = (section) => {
        const variantData = section._productVariantData || { variants: [] };
        const selectedOptions = [];

        section.querySelectorAll('.variant-options').forEach((group) => {
            const selectedButton = group.querySelector('.variant-option-btn.selected');
            if (selectedButton) {
                selectedOptions.push((selectedButton.dataset.value || '').trim());
            }
        });

        return variantData.variants.find((variant) => {
            return Array.isArray(variant.options) && variant.options.every((optionValue, index) => optionValue === selectedOptions[index]);
        }) || null;
    };

    const submitAddToCart = async (section) => {
        const addButton = section.querySelector('[data-product-add-button]');
        if (!addButton || addButton.classList.contains('loading') || addButton.disabled) return;

        const form = addButton.closest('form');
        const variant = getSelectedVariant(section);
        if (!form || !variant) return;

        const addText = section.querySelector('[data-product-add-text]');
        const originalText = addText ? addText.textContent : '';

        addButton.disabled = true;
        addButton.classList.add('loading');

        try {
            const formData = new FormData(form);
            formData.set('id', variant.id);
            if (!formData.has('quantity')) {
                formData.append('quantity', '1');
            }
            formData.append('sections', 'cart-drawer,cart-icon-bubble');
            formData.append('sections_url', window.location.pathname);

            const response = await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
                method: 'POST',
                body: formData,
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            const data = await response.json();
            if (data.status) throw new Error(data.description || 'Error adding to cart');

            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data, bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { open: true }, bubbles: true }));
            window.dispatchEvent(new CustomEvent('cart:item-added', { detail: data, bubbles: true }));
            window.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: data, bubbles: true }));

            if (typeof theme !== 'undefined' && theme.miniCart) {
                theme.miniCart.updateElements();
                theme.miniCart.generateCart();
            }

            if (typeof window.updateCartDrawer === 'function') {
                window.updateCartDrawer(true);
            }

            if (addText) addText.textContent = 'Added!';
        } catch (error) {
            console.error('Product add to cart failed:', error);
            if (addText) addText.textContent = originalText;
        } finally {
            window.setTimeout(() => {
                const currentVariant = getSelectedVariant(section);
                addButton.classList.remove('loading');
                if (currentVariant) {
                    updateAddButtonUi(section, currentVariant);
                } else {
                    addButton.disabled = false;
                    if (addText) addText.textContent = originalText;
                }
            }, 1200);
        }
    };

    const bindOptionUi = (section) => {
        const groups = section.querySelectorAll('.variant-options');
        if (!groups.length) return;

        const activateOption = (button) => {
            const group = button.closest('.variant-options');
            if (!group) return;

            group.querySelectorAll('.variant-option-btn').forEach((candidate) => {
                candidate.classList.remove('selected');
                candidate.setAttribute('aria-pressed', 'false');
            });
            button.classList.add('selected');
            button.setAttribute('aria-pressed', 'true');

            const matchedVariant = findVariantByOptions(section);
            if (matchedVariant) {
                applyVariant(section, matchedVariant);
            }
        };

        section.addEventListener('click', (event) => {
            const button = event.target.closest('.variant-option-btn');
            if (!button || !section.contains(button)) return;
            activateOption(button);
        });

        section.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;

            const button = event.target.closest('.variant-option-btn');
            if (!button || !section.contains(button)) return;

            event.preventDefault();
            activateOption(button);
        });
    };

    const bindCardUi = (section) => {
        const cardsContainer = section.querySelector('.pt2-variant-cards');
        if (!cardsContainer) return;

        const activateCard = (card) => {
            const variantData = section._productVariantData || { variants: [] };
            const matchedVariant = variantData.variants.find((variant) => Number(variant.id) === Number(card.dataset.variantId));
            if (matchedVariant) {
                applyVariant(section, matchedVariant);
            }
        };

        cardsContainer.addEventListener('click', (event) => {
            const card = event.target.closest('.pt2-vcard');
            if (!card || !cardsContainer.contains(card)) return;
            activateCard(card);
        });

        cardsContainer.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;

            const card = event.target.closest('.pt2-vcard');
            if (!card || !cardsContainer.contains(card)) return;

            event.preventDefault();
            activateCard(card);
        });
    };

    const bindAddButton = (section) => {
        const addButton = section.querySelector('[data-product-add-button]');
        if (!addButton) return;

        addButton.addEventListener('click', (event) => {
            event.preventDefault();
            submitAddToCart(section);
        });
    };

    const initSection = (section) => {
        const sectionId = section.dataset.productSection || section.dataset.sectionId;
        section.dataset.productSection = sectionId;
        section._productVariantData = getVariantData(section);

        bindOptionUi(section);
        bindCardUi(section);
        bindAddButton(section);
        syncGalleryCounter(section);

        const selectedVariant = getSelectedVariant(section) || (section._productVariantData.variants || []).find((variant) => variant.available) || section._productVariantData.variants[0];
        if (selectedVariant) {
            applyVariant(section, selectedVariant, { updateUrl: false, dispatchEvent: false });
        }
    };

    const init = () => {
        document.querySelectorAll(PRODUCT_SECTION_SELECTOR).forEach(initSection);
    };

    window.handleVariantChange = function (target) {
        const section = target && target.closest ? target.closest(PRODUCT_SECTION_SELECTOR) : null;
        if (!section || !target) return;
        target.click();
    };

    window.addToCartClinical = function (eventOrButton) {
        const target = eventOrButton && eventOrButton.currentTarget ? eventOrButton.currentTarget : eventOrButton;
        const section = target && target.closest ? target.closest(PRODUCT_SECTION_SELECTOR) : null;
        if (!section) return;
        submitAddToCart(section);
    };

    window.addEventListener('pageshow', () => {
        document.querySelectorAll(`${PRODUCT_SECTION_SELECTOR} [data-product-add-button]`).forEach((button) => {
            button.classList.remove('loading');
            const available = button.dataset.variantAvailable !== 'false';
            button.disabled = !available;
        });

        document.body.classList.remove(
            'loading',
            'no-scroll',
            'menu-open',
            'drawer-open',
            'page-transition',
            'js-drawer-open',
            'js-drawer-open-right',
            'js-drawer-open-left',
            'overflow-hidden'
        );
    });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();

